import { useState, useCallback } from 'react';

export interface WizardStep {
  id: string;
  title: string;
  description?: string;
}

interface WizardProps {
  title: string;
  steps: WizardStep[];
  activeStep: number;
  onClose: () => void;
  onBack: () => void;
  onNext: () => void;
  canAdvance: boolean;
  isLastStep: boolean;
  isSubmitting?: boolean;
  children: React.ReactNode;
}

export function Wizard({
  title,
  steps,
  activeStep,
  onClose,
  onBack,
  onNext,
  canAdvance,
  isLastStep,
  isSubmitting = false,
  children,
}: WizardProps) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">{title}</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
              aria-label="Close wizard"
            >
              &times;
            </button>
          </div>

          {/* Step Indicators */}
          <div className="flex items-center gap-2">
            {steps.map((step, idx) => (
              <div key={step.id} className="flex items-center flex-1">
                <div className="flex items-center gap-2 flex-shrink-0">
                  <div
                    className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                      idx < activeStep
                        ? 'bg-green-500 text-white'
                        : idx === activeStep
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-200 text-gray-500'
                    }`}
                  >
                    {idx < activeStep ? '✓' : idx + 1}
                  </div>
                  <span
                    className={`text-xs font-medium hidden sm:inline ${
                      idx <= activeStep ? 'text-gray-900' : 'text-gray-400'
                    }`}
                  >
                    {step.title}
                  </span>
                </div>
                {idx < steps.length - 1 && (
                  <div
                    className={`flex-1 h-0.5 mx-2 ${
                      idx < activeStep ? 'bg-green-500' : 'bg-gray-200'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-6">{children}</div>

        {/* Footer */}
        <div className="px-6 py-4 border-t flex justify-between">
          <button
            onClick={activeStep === 0 ? onClose : onBack}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            {activeStep === 0 ? 'Cancel' : '← Back'}
          </button>
          <button
            onClick={onNext}
            disabled={!canAdvance || isSubmitting}
            className="px-5 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Working...' : isLastStep ? 'Finish ✓' : 'Next →'}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Hook to manage wizard step state */
export function useWizardSteps(totalSteps: number) {
  const [step, setStep] = useState(0);

  const next = useCallback(() => setStep((s) => Math.min(s + 1, totalSteps - 1)), [totalSteps]);
  const back = useCallback(() => setStep((s) => Math.max(s - 1, 0)), []);
  const reset = useCallback(() => setStep(0), []);

  return { step, next, back, reset, isFirst: step === 0, isLast: step === totalSteps - 1 };
}
